import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import { FileText, Upload, Loader2, CheckCircle, AlertCircle, FileDown, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface PDFPosition {
  positionNumber?: string;
  beschreibung: string;
  menge: number;
  einheit: string;
  einzelpreis: number;
  gesamtpreis: number;
  gewerk?: string;
}

interface PDFImportResult {
  documentType: 'angebot' | 'rechnung' | 'unknown';
  metadata: {
    documentNumber?: string;
    date?: string;
    customerName?: string;
    customerAddress?: string;
    projectName?: string;
    totalNet?: number;
    totalGross?: number;
    vatRate?: number;
    vatAmount?: number;
  };
  positions: PDFPosition[];
  positionCount: number;
}

export default function PDFImport() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<PDFImportResult | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const setQuoteHeader = useOfferStore((state) => state.setQuoteHeader);
  const setQuoteItems = useOfferStore((state) => state.setQuoteItems);
  const setPositions = useOfferStore((state) => state.setPositions);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: 'Ungültiger Dateityp',
        description: 'Bitte wählen Sie eine PDF-Datei aus.',
        variant: 'destructive',
      });
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/import-pdf', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Import fehlgeschlagen');
      }

      setImportResult(result.data);

      toast({
        title: 'PDF analysiert',
        description: `${result.data.positionCount} Positionen gefunden`,
      });

    } catch (error) {
      console.error('PDF import error:', error);
      toast({
        title: 'Import fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportToQuote = () => {
    if (!importResult) return;

    // Set quote header
    setQuoteHeader({
      kunde: importResult.metadata.customerName || '',
      anschrift: importResult.metadata.customerAddress || '',
      angebot_nr: importResult.metadata.documentNumber || `IMP-${Date.now().toString(36).toUpperCase()}`,
      datum: importResult.metadata.date || new Date().toISOString().split('T')[0],
      bauvorhaben: importResult.metadata.projectName || '',
      gewerk: '',
      auftrag: '',
      eingereichte_unterlagen: '',
      lz: '',
    });

    // Convert positions to quote items
    const quoteItems = importResult.positions.map((pos, index) => ({
      id: index + 1,
      originalId: pos.positionNumber || `${index + 1}`,
      positionNumber: pos.positionNumber,
      name: pos.beschreibung.substring(0, 50),
      beschreibung: pos.beschreibung,
      gewerk: pos.gewerk || 'Import',
      einheit: pos.einheit,
      preis: pos.einzelpreis,
      umsatzsteuer: importResult.metadata.vatRate || 19,
      menge: pos.menge,
    }));

    setQuoteItems(quoteItems);

    // Also set positions for database
    const positions = importResult.positions.map((pos, index) => ({
      id: index + 1,
      originalId: pos.positionNumber || `${index + 1}`,
      gewerk: pos.gewerk || 'Import',
      name: pos.beschreibung.substring(0, 50),
      beschreibung: pos.beschreibung,
      einheit: pos.einheit,
      preis: pos.einzelpreis,
      umsatzsteuer: importResult.metadata.vatRate || 19,
    }));

    setPositions(positions);

    toast({
      title: 'Import erfolgreich',
      description: `${quoteItems.length} Positionen wurden importiert`,
    });

    setIsOpen(false);
    setImportResult(null);
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return '-';
    return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="h-8 text-xs gap-1"
      >
        <FileText className="h-3.5 w-3.5" />
        PDF Import
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              PDF Angebot/Rechnung importieren
            </DialogTitle>
            <DialogDescription>
              Laden Sie eine PDF-Datei hoch. Die KI extrahiert automatisch alle Positionen.
            </DialogDescription>
          </DialogHeader>

          {!importResult ? (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {isImporting ? (
                <div className="space-y-4">
                  <Loader2 className="h-12 w-12 mx-auto animate-spin text-blue-600" />
                  <p className="text-gray-600">PDF wird analysiert...</p>
                  <p className="text-sm text-gray-400">Die KI extrahiert Positionen und Metadaten</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-12 w-12 mx-auto text-gray-400" />
                  <div>
                    <p className="text-gray-600">PDF-Datei hier ablegen</p>
                    <p className="text-sm text-gray-400">oder klicken zum Auswählen</p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileInput}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload">
                    <Button variant="outline" asChild>
                      <span>Datei auswählen</span>
                    </Button>
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Metadata Card */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">Erkannte Daten</CardTitle>
                    <Badge variant={importResult.documentType === 'angebot' ? 'default' : 'secondary'}>
                      {importResult.documentType === 'angebot' ? 'Angebot' :
                       importResult.documentType === 'rechnung' ? 'Rechnung' : 'Dokument'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 text-sm">
                  {importResult.metadata.documentNumber && (
                    <div>
                      <span className="text-gray-500">Nummer:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.documentNumber}</span>
                    </div>
                  )}
                  {importResult.metadata.date && (
                    <div>
                      <span className="text-gray-500">Datum:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.date}</span>
                    </div>
                  )}
                  {importResult.metadata.customerName && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Kunde:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.customerName}</span>
                    </div>
                  )}
                  {importResult.metadata.projectName && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Projekt:</span>
                      <span className="ml-2 font-medium">{importResult.metadata.projectName}</span>
                    </div>
                  )}
                  {importResult.metadata.totalNet !== undefined && (
                    <div>
                      <span className="text-gray-500">Netto:</span>
                      <span className="ml-2 font-medium">{formatCurrency(importResult.metadata.totalNet)}</span>
                    </div>
                  )}
                  {importResult.metadata.totalGross !== undefined && (
                    <div>
                      <span className="text-gray-500">Brutto:</span>
                      <span className="ml-2 font-medium">{formatCurrency(importResult.metadata.totalGross)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Positions Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">
                    Positionen ({importResult.positions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Pos</TableHead>
                          <TableHead>Beschreibung</TableHead>
                          <TableHead className="w-20 text-right">Menge</TableHead>
                          <TableHead className="w-16">Einheit</TableHead>
                          <TableHead className="w-24 text-right">EP</TableHead>
                          <TableHead className="w-24 text-right">GP</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importResult.positions.map((pos, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-mono text-xs">
                              {pos.positionNumber || index + 1}
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-xs">
                              {pos.beschreibung}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {pos.menge.toLocaleString('de-DE')}
                            </TableCell>
                            <TableCell className="text-xs">{pos.einheit}</TableCell>
                            <TableCell className="text-right text-xs">
                              {formatCurrency(pos.einzelpreis)}
                            </TableCell>
                            <TableCell className="text-right text-xs font-medium">
                              {formatCurrency(pos.gesamtpreis)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setImportResult(null)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Andere Datei
                </Button>
                <Button
                  onClick={handleImportToQuote}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  In Angebot übernehmen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
